import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { ThemeProvider } from '@/components/layout/ThemeProvider'
import { AuthProvider } from '@/hooks/useAuth'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { WebSocketProvider } from '@/contexts/WebSocketContext'

// Layouts
import { PublicLayout } from '@/components/layout/PublicLayout'
import { FeatureGuide } from '@/components/layout/FeatureGuide'
import { MainLayout } from '@/components/layout/MainLayout'
import { AuthLayout } from '@/components/layout/AuthLayout'

// Pages
const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((module) => ({ default: module.LoginPage }))
)
const RegisterPage = lazy(() =>
  import('@/pages/RegisterPage').then((module) => ({
    default: module.RegisterPage,
  }))
)
const CapitalPage = lazy(() =>
  import('@/pages/CapitalPage').then((module) => ({
    default: module.CapitalPage,
  }))
)
const DemoPage = lazy(() =>
  import('@/pages/CapitalPage').then((module) => ({ default: module.DemoPage }))
)
const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then((module) => ({
    default: module.LandingPage,
  }))
)
const LabPage = lazy(() =>
  import('@/pages/LabPage').then((module) => ({ default: module.LabPage }))
)
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((module) => ({
    default: module.NotFoundPage,
  }))
)
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  }))
)
const PortfoliosPage = lazy(() =>
  import('@/pages/PortfoliosPage').then((module) => ({
    default: module.PortfoliosPage,
  }))
)
const PortfolioDetailPage = lazy(() =>
  import('@/pages/PortfolioDetailPage').then((module) => ({
    default: module.PortfolioDetailPage,
  }))
)
const TransactionsPage = lazy(() =>
  import('@/pages/TransactionsPage').then((module) => ({
    default: module.TransactionsPage,
  }))
)
const CapTablesPage = lazy(() =>
  import('@/pages/CapTablesPage').then((module) => ({
    default: module.CapTablesPage,
  }))
)
const CapTableDetailPage = lazy(() =>
  import('@/pages/CapTableDetailPage').then((module) => ({
    default: module.CapTableDetailPage,
  }))
)
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((module) => ({
    default: module.SettingsPage,
  }))
)
const TaxCalculatorPage = lazy(() =>
  import('@/pages/TaxCalculatorPage').then((module) => ({
    default: module.TaxCalculatorPage,
  }))
)
const DividendCalendarPage = lazy(() =>
  import('@/pages/DividendCalendarPage').then((module) => ({
    default: module.DividendCalendarPage,
  }))
)
const RealEstatePage = lazy(() =>
  import('@/pages/RealEstatePage').then((module) => ({
    default: module.RealEstatePage,
  }))
)
const AdminPage = lazy(() =>
  import('@/pages/AdminPage').then((module) => ({ default: module.AdminPage }))
)
const ImportPage = lazy(() =>
  import('@/pages/ImportPage').then((module) => ({
    default: module.ImportPage,
  }))
)
const ScreenerPage = lazy(() =>
  import('@/pages/ScreenerPage').then((module) => ({
    default: module.ScreenerPage,
  }))
)
const PublicPortfoliosPage = lazy(() =>
  import('@/pages/PublicPortfoliosPage').then((module) => ({
    default: module.PublicPortfoliosPage,
  }))
)
const PublicPortfolioPage = lazy(() =>
  import('@/pages/PublicPortfolioPage').then((module) => ({
    default: module.PublicPortfolioPage,
  }))
)
const CommunityPage = lazy(() =>
  import('@/pages/CommunityPage').then((module) => ({
    default: module.CommunityPage,
  }))
)
const ArticleDetailPage = lazy(() =>
  import('@/pages/ArticleDetailPage').then((module) => ({
    default: module.ArticleDetailPage,
  }))
)
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((module) => ({
    default: module.PricingPage,
  }))
)
const OtherAssetsPage = lazy(() =>
  import('@/pages/OtherAssetsPage').then((module) => ({
    default: module.OtherAssetsPage,
  }))
)
const PipelinesPage = lazy(() =>
  import('@/pages/PipelinesPage').then((module) => ({
    default: module.PipelinesPage,
  }))
)
const PipelineDetailPage = lazy(() =>
  import('@/pages/PipelineDetailPage').then((module) => ({
    default: module.PipelineDetailPage,
  }))
)

const ArticleEditorPage = lazy(() =>
  import('@/pages/ArticleEditorPage').then((module) => ({
    default: module.ArticleEditorPage,
  }))
)

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="portfolio-theme">
      <AuthProvider>
        <Suspense
          fallback={
            <div className="wealth-loading" role="status">
              Открываем страницу…
              <div />
            </div>
          }
        >
          <Routes>
            {/* Public routes (no auth required) */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/lab" element={<LabPage />} />
            <Route
              path="/public"
              element={
                <PublicLayout>
                  <div className="px-5 pt-5 max-w-7xl mx-auto">
                    <FeatureGuide />
                  </div>
                  <PublicPortfoliosPage />
                </PublicLayout>
              }
            />
            <Route
              path="/p/:slug"
              element={
                <PublicLayout>
                  <div className="px-5 pt-5 max-w-7xl mx-auto">
                    <FeatureGuide />
                  </div>
                  <PublicPortfolioPage />
                </PublicLayout>
              }
            />
            <Route
              path="/pricing"
              element={
                <PublicLayout>
                  <div className="px-5 pt-5 max-w-7xl mx-auto">
                    <FeatureGuide />
                  </div>
                  <PricingPage />
                </PublicLayout>
              }
            />

            {/* Auth routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
            </Route>

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route
                element={
                  <WebSocketProvider>
                    <MainLayout />
                  </WebSocketProvider>
                }
              >
                <Route path="/dashboard" element={<CapitalPage />} />
                <Route path="/exchange" element={<DashboardPage />} />
                <Route path="/portfolios" element={<PortfoliosPage />} />
                <Route
                  path="/portfolios/:id"
                  element={<PortfolioDetailPage />}
                />
                <Route path="/transactions" element={<TransactionsPage />} />
                <Route path="/captables" element={<CapTablesPage />} />
                <Route path="/captables/:id" element={<CapTableDetailPage />} />
                <Route path="/tax" element={<TaxCalculatorPage />} />
                <Route path="/dividends" element={<DividendCalendarPage />} />
                <Route path="/screener" element={<ScreenerPage />} />
                <Route path="/realestate" element={<RealEstatePage />} />
                <Route path="/other-assets" element={<OtherAssetsPage />} />
                <Route path="/pipelines" element={<PipelinesPage />} />
                <Route path="/pipelines/:id" element={<PipelineDetailPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/import" element={<ImportPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/community/new" element={<ArticleEditorPage />} />
                <Route
                  path="/community/edit/:slug"
                  element={<ArticleEditorPage />}
                />
                <Route path="/community" element={<CommunityPage />} />
                <Route
                  path="/community/article/:slug"
                  element={<ArticleDetailPage />}
                />
              </Route>
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <Toaster />
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
